import accounting from 'accounting';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import Coins from 'src/components/common/Coins';
import VisualCurrencyIcon from 'src/components/common/VisualCurrencyIcon';
import { GameParamType } from 'src/types/req-dto/game';
import { EnumStatusesOfKyc } from '../store/dialog/types';
import {
  CURRENCY_SYMBOLS,
  PAYMENT_TYPES,
  SPORT_CATEGORIES,
  THEMES,
  XPRESS_PROVIDER_WITH_REDIRECT_HOME,
} from '../utils/constants';

dayjs.extend(duration);
dayjs.extend(advancedFormat);

const getStringWithoutSpace = (str: string): string => {
  //  eslint-disable-next-line
  return str.replace(/\s+/g, '').trim();
};

const checkScreenSize = (): string => {
  if (document.body.clientWidth < 768) {
    return 'mobile';
  }
  return 'desktop';
};

const detectDevice = (): 'desktop' | 'mobile' | 'tablet' => {
  const ua = navigator.userAgent;
  const sizeBasedDetectedDevice = checkScreenSize();

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    sizeBasedDetectedDevice == 'desktop';
    return 'tablet';
  } else if (
    sizeBasedDetectedDevice == 'mobile' ||
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
  ) {
    return 'mobile';
  }

  return 'desktop';
};

const getExtension = (fileName: string): string => {
  const splittedFilaName = fileName.split('.');

  if (splittedFilaName[0].length === fileName.length) {
    return 'font';
  }

  return splittedFilaName[splittedFilaName.length - 1];
};

const getSkinId = (): number => {
  return Number(process.env.REACT_APP_SKIN_ID);
};
const getCmsContentId = (): string => {
  return String(process.env.REACT_APP_CMS_CONTENT_ID);
};

const getField = (values: any[], fieldName: string): any =>
  values.find((targetObj: any) => targetObj.hasOwnProperty(fieldName));

const getCurrencySymbol = (currency: string): string => CURRENCY_SYMBOLS[currency] || '';

const formatCurrency = (
  amount: number,
  currency: string,
  currencySymbol?: string,
  buyin?: boolean,
  t?: any
  // eslint-disable-next-line sonarjs/cognitive-complexity
): string | JSX.Element => {
  if (Boolean(process.env.REACT_APP_VISUAL_CURRENCY_ICON)) {
    return <VisualCurrencyIcon amount={buyin ? -amount : amount} />;
  }
  if ((currency === 'FPP' || currency === 'GOS') && amount >= 0) {
    return <Coins coin={buyin ? -amount : amount} type={currency} />;
  }

  const _getCurrencySymbol = (): string => {
    if (currencySymbol) {
      return currencySymbol + ' ';
    } else {
      if (currency === 'GoS') {
        return t('gos_points') + ' ';
      } else {
        return getCurrencySymbol(currency) + ' ';
      }
    }
  };
  if (amount === 0) {
    return _getCurrencySymbol() + amount;
  }
  if (envBoolean(process.env.REACT_APP_FORMATTED_AMOUNT_WITH_DOT as string)) {
    return accounting
      .formatMoney(buyin ? -amount : amount, _getCurrencySymbol(), {
        precision: 0,
      })
      .replace(/,/g, '.');
  } else {
    return accounting.formatMoney(buyin ? -amount : amount, _getCurrencySymbol());
  }
};

const convertAllTagsArr = (
  categoriesData: any,
  selectedCategory: string,
  homeTag: any,
  tagsMetaInfo: [] = [],
  lng: string
): any => {
  if (tagsMetaInfo) {
    const updatedMetaInfo = tagsMetaInfo.map((item: any) => ({
      ...item,
      tagTrName: item?.translations?.[lng] || item?.tag,
      tagLabel: item?.configs?.label || '',
    }));
    categoriesData?.[selectedCategory]?.tags?.forEach(
      (category: { tagName: string; viewType: string; priority: number; tagTrName: string; tagLabel: string }) => {
        updatedMetaInfo.forEach((tagInfo: Record<string, string>) => {
          if (category.tagName === tagInfo.tag) {
            category.tagTrName = tagInfo.tagTrName;
            category.viewType = tagInfo.viewType;
            category.priority = Number(tagInfo.priority);
            category.tagLabel = tagInfo.tagLabel;
          }
        });
        if (category.tagName === 'allGames') {
          category.priority = 999999;
        }
      }
    );
  }

  const tagsNames: string[] = [];
  const specialTagsNames: any = [];

  if (homeTag?.hasHomeTag) {
    tagsNames.push('home');
  }

  const arr =
    categoriesData?.[selectedCategory]?.tags
      ?.sort(
        (a: { tagName: string; priority: number }, b: { tagName: string; priority: number }) =>
          b?.priority - a?.priority
      )
      ?.map((f: { tagName: string; tagTrName: string }) => f?.tagTrName || f?.tagName) || [];
  tagsNames.push(...arr);

  return {
    allTagNames: tagsNames,
    allTagData: {
      ...categoriesData[selectedCategory],
      tags: [
        ...specialTagsNames,
        ...(categoriesData[selectedCategory]?.tags
          ? categoriesData[selectedCategory]?.tags?.sort(
              (a: { tagName: string; priority: number }, b: { tagName: string; priority: number }) =>
                b?.priority - a?.priority
            )
          : []),
      ],
    },
  };
};

const getRowNumber = (str: string): string => {
  switch (str) {
    case 'oneRow': //TODO correct viewType word from back
      return '2';
    case 'twoRow': //TODO correct viewType word from back
      return '1';
    default:
      return '1';
  }
};

const setParams = (
  user: any,
  game: any,
  skinId: number,
  language: string,
  isMobile: boolean,
  walletId?: string
): any => {
  const params: GameParamType = {
    token: user?.token ?? '',
    id: game?.id,
    userId: user ? user?.id : null,
    skinId,
    walletId,
    lang: language,
    device: isMobile ? 'mobile' : 'desktop',
    lobbyUrl: window?.location?.href ? window.location.href : window.origin,
  };

  if (
    (isMobile && ['Vivo'].includes(game?.provider)) ||
    ['Endorphina', 'Booming'].includes(game?.provider) ||
    (game?.integrator === 'xpress' && XPRESS_PROVIDER_WITH_REDIRECT_HOME.includes(game?.provider))
  ) {
    params.lobbyUrl = location.origin.toString();
  }

  return params;
};

//need to refactor with the help of backend
const getTransactionData = (paymentData: any, user: any, paymentType: any, locale: string): any => {
  const { _id, method, methodId, withRedirection, depositUseHomeUrl, withdrawUseHomeUrl } = paymentData;
  const device = detectDevice();
  const transactionData: any = {};

  transactionData.transactionType = PAYMENT_TYPES[paymentType];
  transactionData.language = locale;
  transactionData.paymentId = _id;
  transactionData.method = method;
  transactionData.methodId = methodId;
  transactionData.withRedirection = withRedirection;
  transactionData.device = device;

  if (user) {
    transactionData.playerId = user.id;
  }

  if ((window && transactionData.transactionType === 'deposit') || transactionData.transactionType === 'DEPOSIT') {
    transactionData.returnUrl = window.origin;
  }

  if (paymentType === 'deposit' && depositUseHomeUrl) {
    transactionData.redirectURL = window.origin;
    transactionData.depositUseHomeUrl = true;
  }

  if (withdrawUseHomeUrl) {
    transactionData.redirectURL = window.origin;
  }

  return transactionData;
};

const onInactive = (cb: () => void, resetCb: () => void, inactivitiTime: number): void => {
  let wait = setTimeout(cb, inactivitiTime);

  const resetTimer = (): void => {
    resetCb();
    clearTimeout(wait);
    wait = setTimeout(cb, inactivitiTime);
  };

  // Listen to activity messages from the child window
  window.addEventListener('message', (event) => {
    if (event.data.name === 'clickAction') {
      resetTimer();
    }
  });

  // Track activity on the main window
  document.onmousedown =
    window.onmouseenter =
    window.onkeypress =
    // window.onmousemove =
    window.onload =
      resetTimer;
};

const isTokenExpired = (): boolean => {
  const now = dayjs();
  const storedDate = localStorage.getItem('storedDate');
  if (storedDate) {
    return now.valueOf() > Number(storedDate);
  }
  return true;
};

const disableInputNumberSymbols = (e: any, type: string, disableNumberSymbols: boolean | undefined): void => {
  if (type === 'number' && ['e', 'E', '+', '-', '.'].includes(e.key) && disableNumberSymbols) {
    e.preventDefault();
  }
};

const groupPayments = (payments: PaymentMethod[]): any => {
  const _group = payments.reduce((paymentGroup: any, method: any) => {
    if (method.hasOwnProperty('group')) {
      (paymentGroup[method['group']] = paymentGroup[method['group']] || []).push(method);
    }
    return paymentGroup;
  }, {});

  if (!!Object.keys(_group).length) return _group;

  return null;
};

const allTagChecker = (array: string[]): string[] | null => {
  if (!Array.isArray(array)) return null;
  return array.length > 0 ? ['all', ...array] : array;
};

const getRewards = (
  personalInfo: any,
  referralLinks: ReferralLinks | null,
  money: any
): { cash: number | string | JSX.Element; spins: number | string } => {
  const notParticipationValue = personalInfo?.referralId || referralLinks?.referralId ? 0 : '-';
  const currency = money ? Object.keys(money).filter((f) => f !== 'GOS')[0] : '';
  const spins = (money.FPP || money.GOS) ?? notParticipationValue;
  const cash = money[currency] ? formatCurrency(money[currency], currency) : notParticipationValue;

  return {
    cash,
    spins,
  };
};

const scrollToTop = (): void => {
  setTimeout(() => {
    const container = document.getElementById('scroll_container');
    const target = container ?? window;
    try {
      target.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } catch {
      target.scrollTo(0, 0);
    }
  }, 0);
};

const replaceAll = (str: string, oldClass: string, newClass: string): string => {
  return str.replace(new RegExp(oldClass, 'g'), newClass);
};

const setBodyOverflow = (type: 'set' | 'unset', isForPage?: boolean): void => {
  const classnameForOthers = 'fixed-body ';
  const classnameForPage = 'fixed-body-by-page ';
  const classname = isForPage ? classnameForPage : classnameForOthers;

  document.body.className =
    type === 'unset'
      ? classname + replaceAll(document.body.className, classname, '')
      : document.body.className.replace(classname, '');
};

// index strated from 0;
const getFromPath = (path: string, separator: string, index = 0): string => {
  if (path.split(separator)[index]) {
    return path.split(separator)[index].includes('?')
      ? path.split(separator)[index].split('?')[0]
      : path.split(separator)[index];
  } else {
    return '';
  }
};

const isJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

const getFromQueryParams = (search: string): { [key: string]: string } => {
  const newStr = search.slice(1);
  const arr = newStr.split('&');
  return arr.reduce((acc, cur) => {
    const d = cur.split('=');
    return {
      ...acc,
      [d[0]]: d[1],
    };
  }, {});
};

const getDocumentNotFoundTranslation = (params: any, translationKey: string): string => {
  const paramsArray = params && Object.keys(params);

  if (['document_not_found', 'hierarchy_permission_error'].includes(translationKey)) {
    return translationKey + '_affiliateId';
  }

  if (paramsArray.includes('email')) {
    return translationKey + '_email';
  } else if (paramsArray.includes('nationalId')) {
    return translationKey + '_nationalId';
  } else if (paramsArray.includes('signupIp')) {
    return translationKey + '_signupIp';
  } else {
    return 'regFailureText';
  }
};

const sliceIntoChunks = (arr: [], chunkSize: number): any => {
  const res = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    res.push(chunk);
  }
  return res;
};

const envStrToBoolean = (envVar?: string): boolean => {
  if (!envVar) return false;
  return /true/.test(envVar);
};

const envBoolean = (envVar: any): boolean => {
  const arg: any = Boolean(envVar);
  return /true/.test(arg);
};

const getFreeSpinMessages = (arr: []): any[] => {
  return arr?.filter((m: any) => {
    if (m.templateId === 'gameboxPrize' && m.data?.mission?.prize?.type === 'freespins') {
      return m;
    }
  });
};

const getBaseUrl = (
  url: string | { baseURL: string; path: string },
  params: any,
  joinQueryArrayValues: any
): string => {
  let path = '';

  if (typeof url === 'string') {
    path = url;
  } else {
    path = url.baseURL + url.path;
  }

  path = params ? path + '?' + joinQueryArrayValues : path;

  return path;
};

// eslint-disable-next-line
const isPaymentDataValid = (
  selectedPayment: any,
  paymentFields: any,
  t: any,
  wallets: any,
  setPaymentError: any,
  paymentError: any
): boolean => {
  const fields = selectedPayment?.fields ?? {};
  const errors: any = {};

  // eslint-disable-next-line
  Object.keys(fields).map((i) => {
    if (fields[i]?.isRequred && !paymentFields?.[i]) {
      errors[i] = t('payment_errors.required_field');
    }

    if (
      fields[i].type === 'number' &&
      fields[i].validation?.range &&
      paymentFields[i] &&
      paymentFields[i] > fields[i].validation?.range.max
    ) {
      errors[i] = t('payment_errors.amount_less_than_max') + fields[i].validation?.range.max;
    }

    if (
      fields[i].type === 'number' &&
      fields[i].validation?.range &&
      paymentFields[i] < fields[i].validation?.range.min
    ) {
      errors[i] = t('payment_errors.amount_greater_than_min') + fields[i].validation?.range.min;
    }

    if (i === 'amount' && selectedPayment?.action === 'withdraw' && paymentFields[i] > wallets?.[0].balance) {
      errors[i] = t('payment_errors.balance_amount');
    }

    if (fields[i].type === 'string' && paymentFields[i] && paymentFields[i].length > fields[i].validation?.range.max) {
      errors[i] = `Length must be less than ${fields[i].validation?.range.max}`;
    }

    if (fields[i]?.addedFields) {
      fields[i]?.addedFields[paymentFields[i]] &&
        Object.keys(fields[i]?.addedFields[paymentFields[i]]).map((m) => {
          if (fields[i].addedFields[paymentFields[i]][m].isRequred && !paymentFields[m] && m !== 'subMethod') {
            errors[m] = t('payment_errors.required_field');
          }
        });
    }
  });

  setPaymentError({ ...paymentError, ...errors });
  return Boolean(errors && !Object.keys(errors)?.length);
};

const configsVariablesHandler = (palette: { [key: string]: string }): void => {
  let palleteStyle = '';

  // Check if palette is undefined
  if (!palette) {
    console.warn('Palette is undefined.');
    return; // Stop execution if palette is undefined
  }

  // If palette is a JSON string, parse it
  if (typeof palette === 'string' && isJsonString(palette)) {
    palette = JSON.parse(palette);
  }

  // At this point, TypeScript knows palette is defined and is an object
  Object.keys(palette).forEach((key: string) => {
    palleteStyle += `${key}:${palette[key]};`;
  });

  // Check if there's a palette in localStorage
  let storagePalette: any = localStorage.getItem('websitePalette');
  storagePalette && (storagePalette = JSON.parse(storagePalette));
  // Apply the style from localStorage or use the generated palette style
  if (storagePalette) {
    document.getElementsByTagName('HTML')[0].setAttribute('style', storagePalette.css);
  } else {
    document.getElementsByTagName('HTML')[0].setAttribute('style', palleteStyle);
  }
};

const mobileClassListHandler = (isMobileState: boolean, selectedCategory: string): any => {
  if (document && isMobileState && SPORT_CATEGORIES.includes(selectedCategory)) {
    const headerEl = document.getElementsByClassName('top-nav-v2')[0];
    const footerEl =
      document.getElementsByClassName('footer-v2')[document.getElementsByClassName('footer-v2').length - 1];
    const contentEl = document.getElementsByClassName('page')[0];
    const containerEl = document.getElementsByClassName('layout--mobile')[0];
    const novusIframeEl = document.getElementsByClassName('sport-book-page')[0];
    const body = document.getElementsByTagName('body')[0];

    headerEl?.classList.add('iosMobileHeader');
    footerEl?.classList.add('iosMobileFooter');
    contentEl?.classList.add('iosMobilePage');
    containerEl?.classList.add('iosMobileContainer');
    novusIframeEl?.classList.add('iosIframe');
    body?.classList.add('iosBody');

    return () => {
      headerEl?.classList.remove('iosMobileHeader');
      footerEl?.classList.remove('iosMobileFooter');
      contentEl?.classList.remove('iosMobilePage');
      containerEl?.classList.remove('iosMobileContainer');
      novusIframeEl?.classList.remove('iosIframe');
      body?.classList.remove('iosBody');
    };
  }
};

const reachTextParser = (str: string): any => <div dangerouslySetInnerHTML={{ __html: str }} />;

const filterData = (data: any, searched: any): any => {
  const filtered: any[] = [];

  data.map((item: any) =>
    item.subCategories.map((subCat: any) =>
      Object.values(subCat).map((el: any) =>
        el.map((miniEl: any) => {
          if (miniEl.title.toLowerCase().includes(searched.toLowerCase())) {
            filtered.push(miniEl);
          }
        })
      )
    )
  );

  return filtered;
};

const hasChildren = (categoriesData: any, category: string, navigation: any): boolean => {
  return Boolean(
    categoriesData[category]?.tags?.length > 1 && navigation?.find((f: any) => f.name === category)?.hasHomeTag
  );
};

const getDefaultTheme = (): SelectOptionType => {
  const DEFAULT_PALLETE = process.env.REACT_APP_DEFAULT_PALLETE;
  if (DEFAULT_PALLETE) {
    return { label: DEFAULT_PALLETE, value: DEFAULT_PALLETE };
  }
  return { label: THEMES[0], value: THEMES[0] };
};

const calculateTimeLeft = (targetDate: Date | string | null): any => {
  const now = dayjs();
  const then = dayjs(targetDate);
  const diffInMilliseconds = then.diff(now);
  const countdown = dayjs.duration(diffInMilliseconds);

  return {
    days: Math.floor(countdown.asDays()) >= 0 ? String(Math.floor(countdown.asDays())).padStart(2, '0') : '00',
    hours: countdown.hours() >= 0 ? String(countdown.hours()).padStart(2, '0') : '00',
    minutes: countdown.minutes() >= 0 ? String(countdown.minutes()).padStart(2, '0') : '00',
    seconds: countdown.seconds() >= 0 ? String(countdown.seconds()).padStart(2, '0') : '00',
  };
};

const isValidDate = (dateString: any): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

const getCookie = (name: string): string => {
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const c = cookies[i].trim();
    if (c.indexOf(name + '=') === 0) {
      return c.substring(name.length + 1);
    }
  }
  return '';
};

const checkStatusOfKyc = (kycStatus: string): boolean => {
  switch (kycStatus) {
    case EnumStatusesOfKyc.PENDING:
    case EnumStatusesOfKyc.REJECTED:
    case EnumStatusesOfKyc.EXPIRED:
      return false;
    case EnumStatusesOfKyc.VERIFIED:
      return true;
    default:
      return false;
  }
};

const checkDeviceCompatibility = (): { compatible: boolean; reason?: string } => {
  const minMemoryGB = 0.2; // Example: Minimum 200MB free memory (in GB)

  // Check for memory
  if ((navigator as any).deviceMemory && (navigator as any).deviceMemory < minMemoryGB) {
    return { compatible: false, reason: 'Insufficient device memory.' };
  }

  // Check for browser compatibility
  const unsupportedBrowser = !('WebSocket' in window && 'Promise' in window);
  if (unsupportedBrowser) {
    return { compatible: false, reason: 'Unsupported browser.' };
  }

  // Add more checks as necessary
  return { compatible: true };
};

const isNumber = (input: any): boolean => {
  if (typeof input === 'number') {
    return !isNaN(input) && isFinite(input);
  }
  if (typeof input === 'string' && input.trim() !== '') {
    return !isNaN(parseFloat(input)) && isFinite(parseFloat(input));
  }
  return false;
};

const removeFromArray = (array: any[], findFn: (i?: any) => boolean): any[] => {
  const index = array.findIndex(findFn);
  const newArray = [...array];
  newArray.splice(index, 1);
  return newArray;
};

const invalidKeys = ['e', 'E', '.', '-', '+'];

const getDeviceName = (): string => {
  if (isMobile) return 'Mobile';
  if (isTablet) return 'Tablet';
  if (isDesktop) return 'Desktop';
  return 'Unknown';
};

const formatLangCode = (str: string): string => {
  if (!str.includes('-')) return str;
  return str.replace(/-([a-z])/i, (_, char) => char.toUpperCase());
};

const hasMissingLimits = (...values: Array<number | null>): boolean => values.some((value) => value === null);

export {
  allTagChecker,
  calculateTimeLeft,
  checkDeviceCompatibility,
  checkStatusOfKyc,
  configsVariablesHandler,
  convertAllTagsArr,
  detectDevice,
  disableInputNumberSymbols,
  envBoolean,
  envStrToBoolean,
  filterData,
  formatCurrency,
  formatLangCode,
  getBaseUrl,
  getCmsContentId,
  getCookie,
  getCurrencySymbol,
  getDefaultTheme,
  getDeviceName,
  getDocumentNotFoundTranslation,
  getExtension,
  getField,
  getFreeSpinMessages,
  getFromPath,
  getFromQueryParams,
  getRewards,
  getRowNumber,
  getSkinId,
  getStringWithoutSpace,
  getTransactionData,
  groupPayments,
  hasChildren,
  hasMissingLimits,
  invalidKeys,
  isJsonString,
  isNumber,
  isPaymentDataValid,
  isTokenExpired,
  isValidDate,
  mobileClassListHandler,
  onInactive,
  reachTextParser,
  removeFromArray,
  scrollToTop,
  setBodyOverflow,
  setParams,
  sliceIntoChunks,
};
